import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Plus, Search, Tag } from "lucide-react";
import { Skeleton } from "@shared/ui/feedback/Skeleton";
import { ErrorState } from "@shared/ui/feedback/ErrorState";
import { EmptyState } from "@shared/ui/feedback/EmptyState";
import { Card } from "@shared/ui/containers/Card";
import { Fab } from "@shared/ui/primitives/Fab";
import { HeroHeader } from "@shared/ui/headers/HeroHeader";
import { PillTabs } from "@shared/ui/lists/PillTabs";
import { pageCopy } from "@shared/lib/pageCopy";
import { cn } from "@shared/lib/cn";
import {
  fetchClassifiedCategories,
  fetchClassifieds,
  fetchMyClassifieds,
  type ClassifiedSummary,
} from "./api";

const ALL_CATEGORY = "";
const LIST_SCOPES = [
  { value: "all", label: "All" },
  { value: "mine", label: "Mine" },
] as const;

function priceLabel(c: ClassifiedSummary): string {
  if (c.price == null) return "Free";
  const currency = c.currency === "ZAR" || !c.currency ? "R" : c.currency;
  return `${currency} ${c.price.toLocaleString()}`;
}

function timeAgo(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const day = 86_400_000;
  if (diff < day) return "Today";
  if (diff < 2 * day) return "Yesterday";
  if (diff < 7 * day) return `${Math.floor(diff / day)} days ago`;
  if (diff < 30 * day) return `${Math.floor(diff / (7 * day))} wk ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });
}

/**
 * Classifieds — categories from API as filter pills; listings from `/classifieds`.
 */
export function Classifieds(): JSX.Element {
  const copy = pageCopy().classifieds;
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState(ALL_CATEGORY);
  const [showMine, setShowMine] = useState(false);

  const categoriesQuery = useQuery({
    queryKey: ["classified-categories"],
    queryFn: fetchClassifiedCategories,
  });

  const listingsQuery = useQuery({
    queryKey: ["classifieds", showMine ? "mine" : "all", categoryId],
    queryFn: () =>
      showMine
        ? fetchMyClassifieds()
        : fetchClassifieds(categoryId || undefined),
  });

  const categories = categoriesQuery.data ?? [];
  const categoryOptions = useMemo(
    () => [
      { value: ALL_CATEGORY, label: "All" },
      ...categories.map((cat) => ({
        value: cat.classified_category_id,
        label: cat.name,
      })),
    ],
    [categories],
  );

  const listings = useMemo(() => {
    const term = search.trim().toLowerCase();
    const all = listingsQuery.data ?? [];
    return all.filter((c) => {
      if (!showMine && categoryId && c.classified_category_id !== categoryId) {
        return false;
      }
      if (!term) return true;
      return [c.title, c.category, c.author_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term));
    });
  }, [listingsQuery.data, search, categoryId, showMine]);

  return (
    <div className="min-h-full bg-paper">
      <HeroHeader
        eyebrow={copy.eyebrow}
        title={copy.title}
        subtitle={copy.subtitle}
      />

      <div className="px-5 pt-4">
        <div className="flex items-center gap-2 rounded-pill bg-card hairline px-4 py-2.5 shadow-card">
          <Search
            className="h-4 w-4 shrink-0 text-ink-muted"
            strokeWidth={1.75}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={copy.searchPlaceholder}
            className="w-full bg-transparent text-body text-ink placeholder:text-ink-muted focus:outline-none"
          />
        </div>
      </div>

      <div className="mt-3 flex items-center justify-between gap-3 px-5">
        <div className="min-w-0 flex-1 pb-1">
          {!showMine ? (
            <>
              {categoriesQuery.isLoading ? (
                <Skeleton className="h-8 w-20 shrink-0 rounded-pill" />
              ) : (
                <PillTabs
                  ariaLabel="Classified categories"
                  options={categoryOptions}
                  value={categoryId}
                  onChange={setCategoryId}
                />
              )}
            </>
          ) : (
            <span className="py-1.5 text-body-sm text-ink-secondary">
              Your listings
            </span>
          )}
        </div>
        <PillTabs
          ariaLabel="Listing scope"
          options={LIST_SCOPES}
          value={showMine ? "mine" : "all"}
          onChange={(nextValue) => {
            setShowMine(nextValue === "mine");
            setCategoryId(ALL_CATEGORY);
          }}
          size="compact"
          className="shrink-0"
        />
      </div>

      <div className="px-5 pt-4 pb-[calc(8rem+env(safe-area-inset-bottom,0px))]">
        {listingsQuery.isLoading ? (
          <div className="grid grid-cols-2 gap-3">
            <Skeleton className="h-60" />
            <Skeleton className="h-60" />
          </div>
        ) : listingsQuery.isError ? (
          <ErrorState onRetry={() => listingsQuery.refetch()} />
        ) : listings.length === 0 ? (
          <EmptyState
            icon={<Tag className="h-7 w-7" strokeWidth={1.5} />}
            title={
              search || categoryId || showMine
                ? "No matches."
                : "Nothing listed yet."
            }
            description={
              search || categoryId || showMine
                ? "Try a different search or filter."
                : "Be the first to post on the marketplace."
            }
          />
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {listings.map((listing) => (
              <ListingCard
                key={listing.classified_id}
                listing={listing}
                onClick={() =>
                  navigate(`/app/classifieds/${listing.classified_id}`)
                }
              />
            ))}
          </div>
        )}
      </div>

      <Fab
        draggable
        onClick={() => navigate("/app/classifieds/new")}
        label="Post a listing"
      >
        <Plus className="h-5 w-5" strokeWidth={2.2} />
      </Fab>
    </div>
  );
}

function ListingCard({
  listing,
  onClick,
}: {
  listing: ClassifiedSummary;
  onClick: () => void;
}): JSX.Element {
  const free = listing.price == null || listing.price === 0;
  const pending = listing.status === "PENDING";

  return (
    <Card
      asChild
      noGrain
      className="overflow-hidden rounded-xl p-0 text-left shadow-none transition-transform duration-fast active:scale-[0.98]"
    >
      <button type="button" onClick={onClick} className="flex w-full flex-col">
        <div className="relative aspect-[4/3] w-full bg-sunken">
          {listing.cover_image_url ? (
            <img
              src={listing.cover_image_url}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-primary-50">
              <Tag className="h-6 w-6 text-primary-600" strokeWidth={1.5} />
            </div>
          )}
          {listing.category ? (
            <span
              className={cn(
                "absolute left-2 top-2 rounded-pill px-2 py-0.5 text-[0.5625rem] font-semibold uppercase tracking-[0.1em] backdrop-blur",
                free
                  ? "bg-brass/90 text-brass-ink"
                  : "bg-white/90 text-primary-800",
              )}
            >
              {listing.category}
            </span>
          ) : null}
          {pending ? (
            <span className="absolute right-2 top-2 rounded-pill bg-warning/90 px-2 py-0.5 text-[0.5625rem] font-semibold uppercase text-ink">
              Pending
            </span>
          ) : null}
        </div>
        <div className="flex flex-col gap-0.5 p-3">
          <p className="font-display text-body leading-tight text-ink line-clamp-2">
            {listing.title}
          </p>
          <p className="mt-0.5 font-display text-headline text-primary-700">
            {priceLabel(listing)}
          </p>
          <p className="text-caption text-ink-muted line-clamp-1">
            {listing.author_name ?? "Resident"}
          </p>
          <p className="text-caption text-ink-muted">
            {timeAgo(listing.posted_at)}
          </p>
        </div>
      </button>
    </Card>
  );
}
